import { UseLoading, UseStatefulFn } from "@/common/composition/loading";
import { once, order_list_by, uniq, uniqby } from "@/fp";
import "./mock/datasources.api.mock";
import * as api from "./datasources.api";
import { Ref, computed, ref } from "@vue/composition-api";
import WalkthroughModalVue from "@/components/modals/walkthrough-modal/WalkthroughModal.vue";
import {
  SourceBrief,
  ConsentAcquisitionDescriptor,
  PasswordAcquisitionDescriptor,
  SourceApplication,
  ApplicationType,
  DataSourceType,
  PostureTabsGroup,
} from "./model";
import { UseFiniteCollectionSearch } from "@/common/composition/use-search";
import { UseTabs } from "@/common/composition/use-tabs";
import { UseModal } from "@/components/root/modal";
import SourceConcentModal from "@/views/customize/data-sources/modals/SourceConcentModal.vue";
import SourcePasswordSetupModal from "@/views/customize/data-sources/modals/SourcePasswordSetupModal.vue";
import { setQueryParams } from "@/utils/url";
import { SharedState } from "@/common/state";
import { createStorageItem } from "@/storage";
import { ToastType, UseToast } from "@/components/root/toast/toast.state";

import {
  APPLICATIONS_MAP,
  SOURCE_TO_APPS_MAP,
  SourceApplicationsDescriptors,
} from "./data-relations/applicaitons.map";
import { UseCurrentSource } from "./current-source.state";
import router from "@/router";
import SourceDetailsModalVue from "../modals/source-details/SourceDetailsModal.vue";
import { EventBus } from "@/eventbus";
import { TimeFrameOption } from "@/common/state/models";
import { FeatureId } from "@/common/models";
const SourceConsentSessionStorage = createStorageItem<{
  source: SourceBrief;
  auth_params?: Record<string, string>;
  connection_name: string;
  descriptor: ConsentAcquisitionDescriptor;
}>("data_source_concent_provider");

const [sources_tabs, selected_source_tab, set_selected_source_tab] = UseTabs<{
  id: PostureTabsGroup;
  name: string;
}>(
  [
    {
      id: "InMotion",
      name: "Info In Motion",
    },
    {
      id: "AtRest",
      name: "Info At Rest",
    },

    {
      id: "CloudApp",
      name: "Cloud Apps",
    },
  ],
  {
    id: "InMotion",
    name: "Info In Motion",
  }
);

const data_sources: Ref<SourceBrief[]> = ref([]);
const current_data_sources = computed(() =>
  data_sources.value.filter(
    // @ts-ignore
    (f) => f.type === selected_source_tab.value.id && !f.hide_in_data_sources
  )
);

//@ts-ignore
window.current_data_sources = current_data_sources;

const available_applications = computed(() => {
  return uniq(
    data_sources.value
      .filter((f) => f.runtime.enabled)
      .map(({ id }) => SOURCE_TO_APPS_MAP[id])
      .flat()
      .filter((f) => f)
  ).reduce((acc, app_id) => {
    acc[app_id] = SourceApplicationsDescriptors[app_id];
    return acc;
  }, {} as Record<ApplicationType, SourceApplication>);
});

const order_sources_by_id = order_list_by<SourceBrief, "id">("id", [
  "msft_365",
  "sp_site_permission",
  "onprem_db",
  "onprem_fs",
  "amazon_s3",
  "az_storage",
  "mssql_db",
  "msft_dynamics",
  "github",
  "snowflake",
]);

// @ts-ignore
const FeatureToDataSourceMapping: Record<DataSourceType, FeatureId> = {
  msft_365: "365_scanner",
  onprem_fs: "on_premises",
  dropbox_storage: "dropbox_storage",
  az_storage: "azure_storage",
  mssql_db: "mssql_db",
  github: "github",
  snowflake: "snowflake",
  msft_dynamics: "msft_dynamics",
  amazon_s3: "aws_storage",
  sp_site_permission: "sp_site_permission",
};

const set_source_licensed_status = (source_id: DataSourceType): boolean => {
  let is_licensed = true;
  const source_feature_key = FeatureToDataSourceMapping[source_id];
  if (source_feature_key) {
    // if the source is feature flag dependent than first of all
    // lets set it to false.
    is_licensed = false;
  }
  if (SharedState.features.value[source_feature_key]) {
    // the feature came back in the features array.
    // so now let's check if it's enabled.
    is_licensed = SharedState.FeatureHelper.is_enabled(source_feature_key);
  }
  return is_licensed;
};

const [load_sources, is_loading_sources] = UseStatefulFn<void, void, void>(
  async () => {
    const res = await api.get_service_brief_list();
    if (res.success) {
      const sources_with_license_indicator = res.value.map((s) => {
        s.runtime.is_licensed = set_source_licensed_status(s.id);
        return s;
      });
      data_sources.value = order_sources_by_id(
        uniqby("id")(sources_with_license_indicator)
      );
    }
  }
);

const [
  search_query,
  is_searching,
  search_items,
  clear_items,
  set_search_items,
] = UseFiniteCollectionSearch<SourceBrief>(
  1,
  (item, q) => item.name.toLowerCase().search(q.toLowerCase()) > -1,
  []
);

const setup_data_source = async (
  source: SourceBrief,
  existing_connection_names: string[] = [],
  loading_callback_for_calling_ui?: (loading: boolean) => void
) => {
  if (source.managedExternally && source.id == "onprem_fs") {
    UseModal().show(WalkthroughModalVue, {
      id: "onprem_fs.connection",
      height: "50rem",
    });
    return;
  }
  loading_callback_for_calling_ui && loading_callback_for_calling_ui(true);
  const res = await api.get_source_acquisition_descriptor(source.id);
  loading_callback_for_calling_ui && loading_callback_for_calling_ui(false);
  if (res.success) {
    if (res.value.type === "Consent") {
      start_concent_modal_flow(source, res.value, existing_connection_names);
    } else {
      start_source_keys_setup_flow(
        source,
        res.value,
        existing_connection_names
      );
    }
  } else {
    UseToast().show({
      type: ToastType.ALERT,
      msg: "Failed to get connection descriptor, try again soon",
      duration: 5000,
    });
  }
};

const setup_session_based_data_source = async (
  source: SourceBrief,
  existing_connection_names: string[] = [],
  loading_callback_for_calling_ui?: (loading: boolean) => void
) => {
  SharedState.ifUserEnabledFeature(
    FeatureToDataSourceMapping[source.id],
    async () => {
      if (source.managedExternally) {
        UseModal().showSimple({
          title: `${source.name} is managed externally`,
          desc: "To set it up, please checkout the details below..",
        });
      } else {
        setup_data_source(
          source,
          existing_connection_names,
          loading_callback_for_calling_ui
        );
      }
    },
    { premium_modal_key: "m365atrest-popup-2024" }
  );
};

const start_source_keys_setup_flow = async (
  source: SourceBrief,
  descriptor: PasswordAcquisitionDescriptor,
  existing_connection_names: string[] = []
) => {
  const modal_res = await UseModal().show(SourcePasswordSetupModal, {
    name: source.name,
    existing_connection_names,
    icon: source.runtime.icon,
    ...descriptor,
  });
  if (modal_res.type === "success") {
    // @ts-ignore
    const fields: Record<string, string> = modal_res.msg.fields;
    // @ts-ignore
    const connection_name: string = modal_res.msg.connection_name;
    console.log("fields,connection_name", modal_res.msg);
    const res = await api.setup_data_source_via_keys({
      connection_name,
      fields,
      source_id: source.id,
    });
    console.log("resres ::", res);
    let msg = "";
    if (res.success) {
      // add the connection to the top
      EventBus.emit("refresh_connection_list");
      if (!source.runtime.enabled) {
        load_sources();
      }
      msg = `Successfully created a connection to ${source.name}`;
    } else {
      switch (res.code) {
        case 455:
          msg = `Bad input, make sure the fields has correct input`;
          break;
        default:
          msg = "unknown error during connection process :(";
          break;
      }
    }
    show_source_setup_message(source.name, res.success, msg);
  }
};

const start_concent_modal_flow = async (
  source: SourceBrief,
  descriptor: ConsentAcquisitionDescriptor,
  existing_connection_names: string[] = []
) => {
  const concent_modal_res = await UseModal().show(SourceConcentModal, {
    id: source.id,
    descriptor,
    existing_connection_names,
    icon: source.runtime.icon,
    name: source.name,
  });
  if (concent_modal_res.type === "success") {
    const { endpointUrl, stateParam } = descriptor;
    const concent_end_point = setQueryParams(endpointUrl, [
      [stateParam, source.id],
      ["redirect_uri", location.origin + "/data-sources-management"],
    ]);
    // @ts-ignore
    const { connection_name, auth_params } = concent_modal_res.msg!;
    SourceConsentSessionStorage.save({
      source,
      auth_params: auth_params || {},
      connection_name: connection_name,
      descriptor,
    });
    window.open(concent_end_point, "_self");
  }
};

const [is_during_concent, set_is_during_concent] = UseLoading(false).as_arr();
const complete_source_concent_flow = async (params: Record<string, string>) => {
  set_is_during_concent(true);
  const {
    source,
    connection_name,
    descriptor,
    auth_params,
  } = SourceConsentSessionStorage.get()!;
  // setting the correct tab
  set_selected_source_tab(
    sources_tabs.value.find((f) => f.id === source.type)!
  );
  // opening the modal for the connections
  const consent = params[descriptor.consentParam]!;
  if (consent) {
    if (source.multiConnectionAllowed) {
      UseModal().show(SourceDetailsModalVue, source);
    }
    const concent_res = await api.setup_source_via_token({
      source_id: source.id,
      connection_name,
      auth_params,
      consent: params[descriptor.consentParam]!,
    });

    let msg = "";
    if (concent_res.success) {
      msg = `Successfully created a connection to ${source.name}`;
    } else {
      switch (concent_res.code) {
        case 455:
          msg = `the ${source.name} auth system didn't approve your access`;
          break;
        default:
          msg = "unknown error during auth process";
          break;
      }
    }
    show_source_setup_message(source.name, concent_res.success, msg);
  }
  set_is_during_concent(false);
};

const show_source_setup_message = (
  source_name: string,
  is_success: boolean,
  msg: string
) => {
  UseToast().show({
    type: is_success ? ToastType.SUCCESS : ToastType.ALERT,
    msg,
    duration: 5000,
  });
};

const init = once(async () => {
  const query_params = SharedState.sessions_query_params.value;
  const has_params = Object.keys(query_params || {}).length > 0;
  if (has_params && SourceConsentSessionStorage.exist()) {
    await complete_source_concent_flow(query_params);
  }
  SourceConsentSessionStorage.remove();
  await load_sources();
  set_search_items([...data_sources.value]);
});

const get_sessions_for_source = async (source_id: DataSourceType) => {
  return await api.get_sessions_for_source(source_id);
};
/* memoize_ttl(
  20,
 
); */

const get_sessions_for_app = async (app_id: ApplicationType) => {
  const { sources } = APPLICATIONS_MAP[app_id]!;
  const at_rest_source_id = sources.filter(
    (s) => data_sources.value.find((d) => d.id == s)!.type === "AtRest"
  )[0]!;
  const res = await get_sessions_for_source(at_rest_source_id);
  return res;
};
const get_source_for_app_by_type = (
  app_id: ApplicationType,
  type: PostureTabsGroup
) => {
  const { sources } = APPLICATIONS_MAP[app_id]!;
  const source = sources.filter((s) => {
    const item = data_sources.value.find((d) => d.id == s);
    return item && item.type === type;
  })[0]!;
  return source;
};

const get_available_apps_for_source = (
  source_id: DataSourceType
): SourceApplication[] => {
  const apps_for_source = Object.entries(SourceApplicationsDescriptors).reduce(
    (acc, [type, val]) => {
      if (val.sources.includes(source_id)) {
        acc.push(val);
      }
      return acc;
    },
    [] as SourceApplication[]
  );
  return apps_for_source;
};

const is_app_data_source_connected = (
  app_id: ApplicationType,
  type: PostureTabsGroup
) => {
  const source_id = get_source_for_app_by_type(app_id, type);
  const source = data_sources.value.find((d) => d.id === source_id);
  console.log("is_app_data_source_connected source :", source);
  return source ? source.runtime.enabled : false;
};

const get_in_motion_source_time_frames = (
  id: DataSourceType
): TimeFrameOption[] => {
  return [...SharedState.timeFrameOptions.value];
};

const open_scan_session_modal_for_source = async (
  source_id: DataSourceType,
  connection_id?: string
): Promise<"do_nothing" | "refresh"> => {
  const source = data_sources.value.find((s) => s.id == source_id)!;

  if (source && source.runtime.enabled) {
    const res = await UseModal().show(SourceDetailsModalVue, {
      ...source,
      action: "start_scan_session",
      connection_id: connection_id,
    });
    if (res.type == "success") {
      return res.msg == "added_session" ? "refresh" : "do_nothing";
    } else {
      return "do_nothing";
    }
  } else {
    router().push({
      name: "DataSourceManagement",
      query: {
        tab: source.type,
        source: source.id,
        action: "setup",
        connection_id: connection_id,
      },
    });
    return "do_nothing";
  }
};

export const state = {
  init,
  ext: {
    get_in_motion_source_time_frames,
    get_sessions_for_source,
    get_sessions_for_app,
    get_source_for_app_by_type,
    get_available_apps_for_source,
    open_scan_session_modal_for_source,
    is_app_data_source_connected,
  },
  is_during_concent,
  sources_tabs,
  available_applications,
  set_selected_source_tab,
  selected_source_tab,
  is_loading_sources,
  all_sources: data_sources,
  display_sources: computed(() =>
    is_searching.value ? search_items.value : current_data_sources.value
  ),

  search_query,
  is_searching,
  clear_items,
  setup_data_source,
  setup_session_based_data_source,
};
